/**
 * Self-contained, read-only access to the deployed Pyramida + PRMD jetton
 * contracts. We intentionally do NOT import the generated Tact wrapper here:
 * pulling that large generated file into the web build's strict `tsc` program
 * trips `noUnusedLocals`/strict errors. Instead we issue raw `runMethod` calls
 * and parse the returned stacks by mirroring the generated getter tuple layout
 * (see contract/build/Pyramida/tact_Pyramida.ts → `loadGetterTuplePyramidaState`
 * and `loadGetterTupleUserView`). The contract ABI is frozen at deploy, so this
 * stays in sync.
 */
import { Address, beginCell, fromNano } from '@ton/core';
import { TonClient } from '@ton/ton';
import { API_BASE, CONTRACT_ADDRESS, TON_API_ENDPOINT, TON_API_KEY, USE_PROXY } from './config';

let cachedClient: TonClient | null = null;
let tonRpcQueue: Promise<void> = Promise.resolve();
let lastTonRpcStartedAt = 0;

const PUBLIC_TONCENTER_INTERVAL_MS = 1_100;

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });
}

function isRateLimited(error: unknown): boolean {
  return (
    error instanceof Error &&
    /\b429\b|too many requests|rate limit/i.test(error.message)
  );
}

export function getClient(): TonClient {
  if (!cachedClient) {
    cachedClient = new TonClient({
      endpoint: TON_API_ENDPOINT,
      apiKey: TON_API_KEY || undefined,
    });
  }
  return cachedClient;
}

export function runTonRpc<T>(operation: () => Promise<T>): Promise<T> {
  const task = tonRpcQueue.then(async () => {
    if (!TON_API_KEY) {
      const waitFor =
        lastTonRpcStartedAt + PUBLIC_TONCENTER_INTERVAL_MS - Date.now();
      if (waitFor > 0) await delay(waitFor);
    }
    lastTonRpcStartedAt = Date.now();

    try {
      return await operation();
    } catch (error) {
      if (!isRateLimited(error)) throw error;
      await delay(PUBLIC_TONCENTER_INTERVAL_MS);
      lastTonRpcStartedAt = Date.now();
      return operation();
    }
  });
  tonRpcQueue = task.then(
    () => undefined,
    () => undefined,
  );
  return task;
}

/** Decoded on-chain Pyramida state (raw bigint / nanoTON units). */
export interface RawPyramidaState {
  bankBalance: bigint; // nanoTON
  past: {
    lastLotteryTime: bigint;
    lastWinnerKey: bigint;
    lastWinnerAddress: Address | null;
    lastWinAmount: bigint; // nanoTON
    totalWinners: bigint;
    totalWinsAmount: bigint; // nanoTON
  };
  round: {
    endTime: bigint;
    totalTickets: bigint;
    participantsCount: bigint;
  };
  draw: { isDrawing: boolean };
  users: {
    lastKey: bigint;
    userCount: bigint;
  };
  jetton: { lastMintTime: bigint; totalMinted: bigint };
  owner: Address;
  jettonMasterAddress: Address | null;
  /** Real spendable prize ceiling (nanoTON): max(0, balance − reserve − min). */
  availableForPrize: bigint;
}

/**
 * Reads the `getPyramidaState` getter. This getter returns a CONSTANT-SIZE
 * global snapshot: only scalar fields, never the on-chain maps (those would make
 * the response grow with state size). A single user's record is read separately
 * via {@link readUserByAddress}. Fields are read in the exact generated order.
 *
 * NOTE: the on-chain get-method name is `getPyramidaState` (the Tact getter
 * name / ABI name), NOT the generated TS wrapper method `getGetPyramidaState`.
 */
export async function readPyramidaState(): Promise<RawPyramidaState> {
  return USE_PROXY ? readPyramidaStateProxy() : readPyramidaStateRpc();
}

async function readPyramidaStateRpc(): Promise<RawPyramidaState> {
  const client = getClient();
  const address = Address.parse(CONTRACT_ADDRESS);
  const { stack } = await client.runMethod(address, 'getPyramidaState');

  // lotteryBank
  const bankBalance = stack.readBigNumber();
  // pastLotteryInfo
  const lastLotteryTime = stack.readBigNumber();
  const lastWinnerAddress = stack.readAddressOpt();
  const lastWinAmount = stack.readBigNumber();
  const totalWinners = stack.readBigNumber();
  const totalWinsAmount = stack.readBigNumber();
  // currentRound (summary: scalars only)
  const endTime = stack.readBigNumber();
  const totalTickets = stack.readBigNumber();
  const participantsCount = stack.readBigNumber();
  // drawState
  const isDrawing = stack.readBoolean();
  stack.readBigNumber(); // cursor
  stack.readBigNumber(); // accumulated
  stack.readBigNumber(); // randomTarget
  stack.readAddressOpt(); // winnerAddress
  stack.readBoolean(); // winnerFound
  stack.readBigNumber(); // snapshotParticipants
  stack.readBigNumber(); // prizeAmount
  stack.readBigNumber(); // ownerShareAmount
  stack.readBigNumber(); // lastProgressTime
  // jettonRewardsStats
  const jLastMintTime = stack.readBigNumber();
  const totalMinted = stack.readBigNumber();
  // tail
  const owner = stack.readAddress();
  const jettonMasterAddress = stack.readAddressOpt();
  const profileRegistrar = stack.readAddress();
  stack.readBigNumber(); // pendingRefCount
  stack.readBigNumber(); // pendingRefTotal
  const availableForPrize = stack.readBigNumber();

  // Users with an invite key: the registrar issues keys sequentially, so its
  // next-key counter doubles as the user count on the direct-RPC path.
  let lastKey = 0n;
  try {
    const reg = await client.runMethod(profileRegistrar, 'nextInviteKey');
    const next = reg.stack.readBigNumber();
    lastKey = next > 0n ? next - 1n : 0n;
  } catch {
    lastKey = 0n;
  }
  const userCount = lastKey;

  return {
    bankBalance,
    past: {
      lastLotteryTime,
      lastWinnerKey: 0n,
      lastWinnerAddress:
        lastWinnerAddress && !isZeroAddress(lastWinnerAddress) ? lastWinnerAddress : null,
      lastWinAmount,
      totalWinners,
      totalWinsAmount,
    },
    round: { endTime, totalTickets, participantsCount },
    draw: { isDrawing },
    users: { lastKey, userCount },
    jetton: { lastMintTime: jLastMintTime, totalMinted },
    owner,
    jettonMasterAddress,
    availableForPrize,
  };
}

/** A single user's record, as returned by the `getUserByAddress` getter. */
export interface UserView {
  registered: boolean;
  key: bigint;
  referrerKey: bigint;
  address: Address;
  referralsCount: bigint;
  earningsFromReferrals: bigint; // nanoTON
  lastActivity: bigint; // unix seconds
  /**
   * This wallet's stake (ticket weight) in the CURRENT round, in nanoTON.
   * Only available via the proxy (derived from the indexed buy transactions);
   * `null` on the direct-RPC path, which has no per-round history.
   */
  roundStakeNano: bigint | null;
}

/**
 * Reads one user's record by address via the `getUserByAddress` getter. This is
 * the scalable per-user path: an O(1) on-chain map lookup that returns a single
 * small struct, instead of downloading the whole user map. `registered` is false
 * (with zeroed fields) when the address has never bought a ticket.
 */
export async function readUserByAddress(userAddress: Address): Promise<UserView> {
  return USE_PROXY ? readUserByAddressProxy(userAddress) : readUserByAddressRpc(userAddress);
}

const isZeroAddress = (a: Address): boolean => a.hash.every((b) => b === 0);

interface ProfileRead {
  referrer: Address | null;
  refKey: bigint;
  referralsCount: bigint;
  lastActivity: bigint;
}

/** Reads a UserProfile's `profile()` getter; null when not deployed. */
async function readProfileRpc(profileAddr: Address): Promise<ProfileRead | null> {
  try {
    const { stack } = await getClient().runMethod(profileAddr, 'profile');
    stack.readBigNumber(); // queryId
    stack.readAddress(); // user
    const referrer = stack.readAddressOpt();
    stack.readBigNumber(); // registeredAt
    const lastActivity = stack.readBigNumber();
    stack.readAddress(); // registeredBy
    const refKey = stack.readBigNumber();
    const referralsCount = stack.readBigNumber();
    return { referrer, refKey, referralsCount, lastActivity };
  } catch {
    return null; // profile contract not deployed => not registered
  }
}

async function profileAddressOfRpc(userAddress: Address): Promise<Address> {
  const { stack } = await getClient().runMethod(
    Address.parse(CONTRACT_ADDRESS),
    'getProfileAddress',
    [{ type: 'slice', cell: beginCell().storeAddress(userAddress).endCell() }],
  );
  return stack.readAddress();
}

// The user record lives in the identity layer: the wallet's deterministic
// UserProfile contract. The referrer's numeric key takes one extra hop into
// the referrer's own profile. `earningsFromReferrals` is not tracked on-chain
// anymore; it stays 0 on the RPC path (the proxy path reports indexer data).
async function readUserByAddressRpc(userAddress: Address): Promise<UserView> {
  const profile = await readProfileRpc(await profileAddressOfRpc(userAddress));
  if (!profile) {
    return {
      registered: false,
      key: 0n,
      referrerKey: 0n,
      address: userAddress,
      referralsCount: 0n,
      earningsFromReferrals: 0n,
      lastActivity: 0n,
      roundStakeNano: null,
    };
  }
  let referrerKey = 0n;
  if (profile.referrer) {
    const refProfile = await readProfileRpc(await profileAddressOfRpc(profile.referrer));
    if (refProfile) referrerKey = refProfile.refKey;
  }
  return {
    registered: true,
    key: profile.refKey,
    referrerKey,
    address: userAddress,
    referralsCount: profile.referralsCount,
    earningsFromReferrals: 0n,
    lastActivity: profile.lastActivity,
    roundStakeNano: null,
  };
}

// --- Proxy-backed reads (used when VITE_API_BASE is configured) ---
// The backend returns the same fields as JSON strings (see backend/src/ton.ts);
// here we map them back to the bigint / Address shapes the UI expects.

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) {
    throw new Error(`proxy ${path} -> HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

interface ProxyStateResponse {
  state: {
    bankBalance: string;
    past: {
      lastLotteryTime: string;
      lastWinnerKey: string;
      lastWinnerAddress: string | null;
      lastWinAmount: string;
      totalWinners: string;
      totalWinsAmount: string;
    };
    round: { endTime: string; totalTickets: string; participantsCount: string };
    draw: { isDrawing: boolean };
    users: { lastKey: string; userCount: string };
    jetton: { lastMintTime: string; totalMinted: string };
    owner: string;
    jettonMasterAddress: string | null;
    availableForPrize: string;
  };
}

async function readPyramidaStateProxy(): Promise<RawPyramidaState> {
  const { state: s } = await fetchJson<ProxyStateResponse>('/api/state');
  return {
    bankBalance: BigInt(s.bankBalance),
    past: {
      lastLotteryTime: BigInt(s.past.lastLotteryTime),
      lastWinnerKey: BigInt(s.past.lastWinnerKey),
      lastWinnerAddress: s.past.lastWinnerAddress ? Address.parse(s.past.lastWinnerAddress) : null,
      lastWinAmount: BigInt(s.past.lastWinAmount),
      totalWinners: BigInt(s.past.totalWinners),
      totalWinsAmount: BigInt(s.past.totalWinsAmount),
    },
    round: {
      endTime: BigInt(s.round.endTime),
      totalTickets: BigInt(s.round.totalTickets),
      participantsCount: BigInt(s.round.participantsCount),
    },
    draw: { isDrawing: s.draw.isDrawing },
    users: { lastKey: BigInt(s.users.lastKey), userCount: BigInt(s.users.userCount) },
    jetton: { lastMintTime: BigInt(s.jetton.lastMintTime), totalMinted: BigInt(s.jetton.totalMinted) },
    owner: Address.parse(s.owner),
    jettonMasterAddress: s.jettonMasterAddress ? Address.parse(s.jettonMasterAddress) : null,
    availableForPrize: BigInt(s.availableForPrize ?? '0'),
  };
}

interface ProxyUserResponse {
  user: {
    registered: boolean;
    key: string;
    referrerKey: string;
    address: string;
    referralsCount: string;
    earningsFromReferrals: string;
    lastActivity: string;
  };
  round?: { stakeNano: string };
}

async function readUserByAddressProxy(userAddress: Address): Promise<UserView> {
  const { user: u, round } = await fetchJson<ProxyUserResponse>(
    `/api/user/${encodeURIComponent(userAddress.toString())}`,
  );
  return {
    registered: u.registered,
    key: BigInt(u.key),
    referrerKey: BigInt(u.referrerKey),
    address: Address.parse(u.address),
    referralsCount: BigInt(u.referralsCount),
    earningsFromReferrals: BigInt(u.earningsFromReferrals),
    lastActivity: BigInt(u.lastActivity),
    roundStakeNano: round ? BigInt(round.stakeNano) : null,
  };
}

/**
 * Reads the PRMD jetton master's supply data. Proxy path (production): served
 * by our backend's /api/jetton/:master; direct `get_jetton_data()` RPC only
 * when no proxy is configured (local dev).
 */
export async function readJettonMaster(master: Address): Promise<{ totalSupply: bigint; mintable: boolean }> {
  if (USE_PROXY) {
    const j = await fetchJson<{ totalSupply: string; mintable: boolean }>(
      `/api/jetton/${encodeURIComponent(master.toString())}`,
    );
    return { totalSupply: BigInt(j.totalSupply), mintable: j.mintable };
  }
  const { stack } = await getClient().runMethod(master, 'get_jetton_data');
  const totalSupply = stack.readBigNumber();
  const mintable = stack.readBoolean();
  return { totalSupply, mintable };
}

/**
 * Reads the connected wallet's PRMD balance.
 *
 * Proxy path (preferred): one call to `/api/balance/:addr`, served from the
 * backend's DB cache (constant toncenter cost regardless of audience, and the
 * browser never hits toncenter directly). Direct-RPC path (no proxy configured):
 * resolve the jetton-wallet via the master, then read `get_wallet_data()`.
 * Returns 0 if the wallet has not been deployed yet (user holds no PRMD).
 */
export async function readPrmdBalance(owner: Address, master?: Address | null): Promise<bigint> {
  if (USE_PROXY) {
    return readPrmdBalanceProxy(owner);
  }
  if (!master) return 0n; // RPC path needs the master to derive the wallet
  return readPrmdBalanceRpc(master, owner);
}

async function readPrmdBalanceProxy(owner: Address): Promise<bigint> {
  const { balanceNano } = await fetchJson<{ balanceNano: string }>(
    `/api/balance/${encodeURIComponent(owner.toString())}`,
  );
  return BigInt(balanceNano);
}

async function readPrmdBalanceRpc(master: Address, owner: Address): Promise<bigint> {
  const client = getClient();
  const res = await client.runMethod(master, 'get_wallet_address', [
    { type: 'slice', cell: beginCell().storeAddress(owner).endCell() },
  ]);
  const walletAddress = res.stack.readAddress();
  try {
    const { stack } = await client.runMethod(walletAddress, 'get_wallet_data');
    return stack.readBigNumber();
  } catch {
    return 0n; // wallet not deployed -> zero balance
  }
}

export interface LeaderboardEntry {
  key: number;
  count: number;
}
export interface LeaderboardResult {
  items: LeaderboardEntry[];
  /** The caller's own standing, when a `key` was provided (null otherwise). */
  you: { key: number; count: number; rank: number } | null;
}

/**
 * Top referrers by number of signups they brought in. Backend-only (needs the
 * indexer); returns null when no API proxy is configured so callers can hide the
 * leaderboard on pure-RPC deployments. `myKey` optionally asks for the caller's
 * own rank even when outside the returned top-N.
 */
export async function fetchLeaderboard(
  limit = 10,
  myKey?: number | null,
): Promise<LeaderboardResult | null> {
  if (!USE_PROXY) return null;
  const keyQ = myKey != null ? `&key=${encodeURIComponent(String(myKey))}` : '';
  const raw = await fetchJson<{
    items: { key: string; count: number }[];
    you: { key: string; count: number; rank: number } | null;
  }>(`/api/leaderboard?limit=${limit}${keyQ}`);
  return {
    items: raw.items.map((i) => ({ key: Number(i.key), count: i.count })),
    you: raw.you ? { key: Number(raw.you.key), count: raw.you.count, rank: raw.you.rank } : null,
  };
}

export const toTon = (nano: bigint): number => Number(fromNano(nano));
